import { BigNumber } from 'ethers'
import { useState } from 'react'
import { MAX_VESIS_WEEKS, SECONDS_IN_WEEK } from '~/lib/constants'
import { SimpleFraction } from '~/lib/SimpleFraction'
import { Locked } from '../useLockChangeDetails'

interface Stats {
	lockAmount: BigNumber
	totalRewards: BigNumber
	weeklyRewards: BigNumber
}

interface Result {
	stats?: Stats
	change?: {
		totalRewards: BigNumber
		weeklyRewards: BigNumber
	}
}

function calculateStats(
	locked: Locked,
	now: number,
	additionalSISAmount = BigNumber.from(0)
): Stats {
	const duration = new SimpleFraction(
		locked.end.sub(now),
		SECONDS_IN_WEEK
	).toBigNumber()

	const additionalVESISAmount = duration
		.mul(additionalSISAmount)
		.div(MAX_VESIS_WEEKS)
	const newVESISAmount = additionalVESISAmount.add(locked.veSISBalance)

	const weeklyRewards = newVESISAmount
		.mul(locked.tokensPerWeek)
		.div(locked.totalVESISSupply)

	const totalRewards = weeklyRewards.mul(duration)

	return {
		lockAmount: locked.amount.add(additionalSISAmount),
		totalRewards,
		weeklyRewards,
	}
}

export function useNewAmountChanges(
	locked: Locked | undefined,
	amount?: BigNumber
): Result {
	const [now] = useState(() => Math.floor(Date.now() / 1000))

	if (!locked) {
		return {}
	}

	const stats = calculateStats(locked, now)

	if (!amount) {
		return {
			stats,
		}
	}

	const newStats = calculateStats(locked, now, amount)

	return {
		stats: newStats,
		change: {
			totalRewards: newStats.totalRewards.sub(stats.totalRewards),
			weeklyRewards: newStats.weeklyRewards.sub(stats.weeklyRewards),
		},
	}
}
